import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import client from '../api/client';
import { Network, Link2, Plus, Copy, Trash2, Link as LinkIcon, BarChart3, Clock, Percent, Users, User as UserIcon, CheckCircle2, XCircle, AlertCircle, Phone, Send, Briefcase, Target, Edit3, Save, X, ExternalLink, MoreVertical } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

const Referrals = () => {
    const queryClient = useQueryClient();
    const { user: currentUser } = useAuth();
    const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'superadmin';
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [newLink, setNewLink] = useState({ 
        name: '', 
        code: '', // ADDED
        target_type: 'bot', 
        target_url: '', 
        reward_percent: 10, 
        cookie_days: 30,
        owner_id: '',
        owner_lead_id: ''
    });

    const { data: referrals = [], isLoading } = useQuery({
        queryKey: ['referrals'],
        queryFn: async () => (await client.get('/referrals')).data
    });

    const { data: users = [] } = useQuery({
        queryKey: ['users-partners'],
        queryFn: async () => (await client.get('/users')).data,
        enabled: isAdmin
    });

    const { data: leads = [] } = useQuery({
        queryKey: ['leads-partners'],
        queryFn: async () => (await client.get('/crm/leads')).data,
        enabled: isAdmin
    });

    const createMutation = useMutation({
        mutationFn: (data) => {
            // Clean empty strings to null for backend
            const cleanData = {
                ...data,
                owner_id: data.owner_id || null,
                owner_lead_id: data.owner_lead_id || null
            };
            return client.post('/referrals', cleanData);
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['referrals']);
            setIsCreateModalOpen(false);
            setNewLink({ name: '', code: '', target_type: 'bot', target_url: '', reward_percent: 10, cookie_days: 30, owner_id: '', owner_lead_id: '' });
            toast.success('Реферальная ссылка создана');
        }
    });

    const deleteMutation = useMutation({
        mutationFn: (id) => client.delete(`/referrals/${id}`),
        onSuccess: () => {
            queryClient.invalidateQueries(['referrals']);
            toast.success('Ссылка удалена');
        }
    });

    const [selectedReferral, setSelectedReferral] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editData, setEditData] = useState(null);

    const updateLinkMutation = useMutation({
        mutationFn: (data) => client.patch(`/referrals/${data.id}`, data),
        onSuccess: () => {
            queryClient.invalidateQueries(['referrals']);
            setSelectedReferral(null);
            setIsEditMode(false);
            toast.success('Настройки ссылки обновлены');
        }
    });

    const updateStatusMutation = useMutation({
        mutationFn: ({ id, status }) => client.patch(`/referrals/${id}/status`, { status }),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries(['referrals']);
            toast.success(variables.status === 'active' ? 'Заявка одобрена' : 'Заявка отклонена');
        }
    });

    const handleCopy = (code, type, target) => {
        let finalUrl = '';
        if (type === 'bot') {
            // FIX: Ensure Telegram links use t.me/ prefix
            const botName = (target || 'mcc_bot').replace('https://', '').replace('t.me/', '').replace('@', '');
            finalUrl = `https://t.me/${botName}?start=${code}`;
        } else if (type === 'landing') {
            const landing = target || 'millionconnections.club';
            const base = landing.includes('http') ? landing : `https://${landing}`;
            finalUrl = `${base.replace(/\/$/, '')}?ref=${code}`;
        } else {
            const base = target.includes('http') ? target : `https://${target}`;
            finalUrl = `${base.replace(/\/$/, '')}?ref=${code}`;
        }
        navigator.clipboard.writeText(finalUrl);
        toast.success('Ссылка скопирована!');
    };

    const handleCreate = (e) => {
        e.preventDefault();
        createMutation.mutate(newLink);
    };

    return (
        <div className="p-4 md:p-8 space-y-6 md:space-y-8 animate-fade-in pb-24 h-full overflow-y-auto">
            <Toaster position="bottom-right" />
            
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 bg-zinc-950 p-6 md:p-8 rounded-[2rem] border border-zinc-800/50 shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-600/10 blur-[100px] rounded-full pointer-events-none" />
                <div className="flex items-center gap-4 md:gap-6 relative z-10">
                    <div className="w-16 h-16 md:w-20 md:h-20 bg-emerald-600 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-600/30 transform rotate-3">
                        <Network className="w-8 h-8 md:w-10 md:h-10 text-white transform -rotate-3" />
                    </div>
                    <div>
                        <h1 className="text-3xl md:text-5xl font-black text-white tracking-tighter uppercase drop-shadow-md">
                            Партнерка
                        </h1>
                        <p className="text-zinc-400 font-bold mt-2 text-sm md:text-base tracking-widest uppercase flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                            Реферальная программа
                        </p>
                    </div>
                </div>
                <button 
                    onClick={() => setIsCreateModalOpen(true)}
                    className="relative z-10 px-6 py-4 bg-white hover:bg-zinc-200 text-black rounded-2xl font-black uppercase tracking-widest text-xs transition-all flex items-center justify-center gap-2 shadow-xl shadow-white/10 active:scale-95"
                >
                    <Plus className="w-5 h-5" /> Создать ссылку
                </button>
            </div>

            {isAdmin && referrals.filter(l => l.status === 'pending').length > 0 && (
                <div className="space-y-6">
                    <div className="flex items-center gap-3">
                        <AlertCircle className="w-6 h-6 text-amber-500" />
                        <h2 className="text-xl font-black text-white uppercase tracking-wider">Заявки на модерацию</h2>
                        <span className="bg-amber-500 text-black text-[10px] font-black px-2 py-0.5 rounded-full">
                            {referrals.filter(l => l.status === 'pending').length}
                        </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {referrals.filter(l => l.status === 'pending').map(link => (
                            <div key={link.id} className="bg-zinc-900 border-2 border-amber-500/30 p-6 rounded-3xl flex flex-col gap-5 relative overflow-hidden group shadow-xl">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/5 blur-3xl pointer-events-none" />
                                
                                <div className="flex justify-between items-start relative z-10">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <h3 className="text-lg font-black text-white uppercase tracking-wider truncate">{link.owner_name}</h3>
                                            {link.owner_username && (
                                                <a 
                                                    href={`https://t.me/${link.owner_username.replace('@', '')}`} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    className="text-blue-400 hover:text-blue-300 transition-colors"
                                                    title="Перейти в Telegram"
                                                >
                                                    <Send className="w-4 h-4" />
                                                </a>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2 mt-1">
                                            <a 
                                                href={`tel:${link.owner_phone}`} 
                                                className="text-xs text-zinc-400 font-bold hover:text-emerald-400 transition-colors flex items-center gap-1.5"
                                            >
                                                <Phone className="w-3 h-3" /> {link.owner_phone || 'Нет номера'}
                                            </a>
                                        </div>
                                    </div>
                                    <div className="text-[10px] font-black text-amber-500 bg-amber-500/10 px-3 py-1.5 rounded-xl border border-amber-500/20 uppercase tracking-tighter shrink-0">
                                        Заявка
                                    </div>
                                </div>

                                <div className="space-y-4 py-2 relative z-10">
                                    <div className="bg-zinc-950/50 p-3 rounded-2xl border border-zinc-800/50 group-hover:border-amber-500/20 transition-colors">
                                        <div className="text-[10px] text-zinc-500 font-black uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
                                            <Briefcase className="w-3 h-3 text-amber-500/50" /> Ниша / Проект
                                        </div>
                                        <div className="text-zinc-200 text-xs font-bold leading-relaxed">
                                            {link.owner_niche || 'Не указана'}
                                        </div>
                                    </div>

                                    <div className="bg-zinc-950/50 p-3 rounded-2xl border border-zinc-800/50 group-hover:border-amber-500/20 transition-colors">
                                        <div className="text-[10px] text-zinc-500 font-black uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
                                            <Target className="w-3 h-3 text-amber-500/50" /> Аудитория
                                        </div>
                                        <div className="text-zinc-300 text-[11px] font-medium leading-relaxed italic line-clamp-3">
                                            {link.owner_audience ? `"${link.owner_audience}"` : 'Описание отсутствует'}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex gap-3 relative z-10 pt-2">
                                    <button 
                                        onClick={() => updateStatusMutation.mutate({ id: link.id, status: 'active' })}
                                        disabled={updateStatusMutation.isPending}
                                        className="flex-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 shadow-lg shadow-emerald-600/20 active:scale-95 disabled:opacity-50"
                                    >
                                        <CheckCircle2 className="w-4 h-4" /> Одобрить
                                    </button>
                                    <button 
                                        onClick={() => updateStatusMutation.mutate({ id: link.id, status: 'rejected' })}
                                        disabled={updateStatusMutation.isPending}
                                        className="flex-1 py-3 bg-rose-600/10 hover:bg-rose-600/20 text-rose-500 border border-rose-500/20 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50"
                                    >
                                        <XCircle className="w-4 h-4" /> Отказать
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="flex items-center justify-between gap-4 mt-8">
                <h2 className="text-xl font-black text-white uppercase tracking-wider">Все ссылки</h2>
                <div className="flex gap-2">
                    <span className="flex items-center gap-2 px-3 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-[10px] font-bold text-emerald-500 tracking-wider">
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> {referrals.filter(l => l.status === 'active').length} АКТИВНЫХ
                    </span>
                    {isAdmin && (
                        <span className="flex items-center gap-2 px-3 py-1 bg-amber-500/10 border border-amber-500/20 rounded-lg text-[10px] font-bold text-amber-500 tracking-wider">
                            <AlertCircle className="w-3 h-3" /> {referrals.filter(l => l.status === 'pending').length} НА МОДЕРАЦИИ
                        </span>
                    )}
                </div>
            </div>

            {/* Main List Display */}
            <div className="bg-zinc-900/30 border border-zinc-800/50 rounded-[2.5rem] overflow-hidden backdrop-blur-xl shadow-2xl">
                {isLoading ? (
                    <div className="py-24 text-center">
                        <div className="flex flex-col items-center gap-4">
                            <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
                            <p className="text-zinc-500 font-bold uppercase tracking-widest text-[10px]">Загрузка данных...</p>
                        </div>
                    </div>
                ) : referrals.length === 0 ? (
                    <div className="py-24 text-center">
                        <div className="flex flex-col items-center gap-4 text-zinc-600">
                            <LinkIcon className="w-12 h-12 opacity-20" />
                            <p className="font-bold uppercase tracking-widest text-xs">Ссылок не найдено</p>
                        </div>
                    </div>
                ) : (
                    <div className="divide-y divide-zinc-800/30">
                        {/* Desktop Header */}
                        <div className="hidden md:flex items-center px-8 py-6 border-b border-zinc-800/50 bg-zinc-950/20">
                            <div className="flex-1 text-[10px] font-black text-zinc-500 uppercase tracking-widest">Ссылка / Кампания</div>
                            <div className="w-32 text-[10px] font-black text-zinc-500 uppercase tracking-widest">Переходы</div>
                            <div className="w-32 text-[10px] font-black text-zinc-500 uppercase tracking-widest">Лиды</div>
                            <div className="w-24 text-[10px] font-black text-zinc-500 uppercase tracking-widest text-center">Reward</div>
                            <div className="w-40 text-[10px] font-black text-zinc-500 uppercase tracking-widest text-center">Статус</div>
                            <div className="w-24 text-[10px] font-black text-zinc-500 uppercase tracking-widest text-right">Действия</div>
                        </div>

                        {/* List Items */}
                        {referrals
                            .filter(l => isAdmin || l.status === 'active')
                            .map(link => (
                                <div 
                                    key={link.id} 
                                    onClick={() => setSelectedReferral(link)}
                                    className="group flex flex-col md:flex-row md:items-center px-6 py-6 md:px-8 hover:bg-white/[0.03] transition-all cursor-pointer relative"
                                >
                                    {/* Link & Name */}
                                    <div className="flex-1 min-w-0 flex items-center gap-4 mb-4 md:mb-0">
                                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center border shrink-0 ${
                                            link.target_type === 'bot' 
                                            ? 'bg-blue-500/10 border-blue-500/20 text-blue-400' 
                                            : 'bg-purple-500/10 border-purple-500/20 text-purple-400'
                                        }`}>
                                            {link.target_type === 'bot' ? <Send className="w-6 h-6" /> : <ExternalLink className="w-6 h-6" />}
                                        </div>
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm md:text-base font-black text-white uppercase tracking-tight truncate">{link.name}</span>
                                                {isAdmin && <span className="text-[9px] bg-zinc-800 text-zinc-500 px-1.5 py-0.5 rounded font-bold shrink-0">{link.code.split('_')[1] || link.code}</span>}
                                            </div>
                                            <div className="flex items-center gap-1.5 mt-1 overflow-hidden">
                                                <span className="text-[10px] font-bold text-zinc-500 font-mono tracking-tighter opacity-50 truncate">/{link.code}</span>
                                                <span className="text-[8px] uppercase font-black text-zinc-600 bg-zinc-900 border border-zinc-800 px-1.5 py-0.5 rounded shrink-0">
                                                    {link.owner_name}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Stats (Mobile Grid / Desktop Fixed) */}
                                    <div className="grid grid-cols-2 md:contents gap-4 mb-6 md:mb-0">
                                        {/* Clicks */}
                                        <div className="md:w-32 flex flex-col md:block">
                                            <span className="md:hidden text-[8px] font-black text-zinc-600 uppercase tracking-widest mb-1 leading-none">Переходы</span>
                                            <div className="flex items-center gap-2">
                                                <span className="text-base font-black text-white">{link.clicks || 0}</span>
                                                <BarChart3 className="w-3 h-3 text-zinc-700 md:hidden lg:inline" />
                                            </div>
                                        </div>

                                        {/* Leads */}
                                        <div className="md:w-32 flex flex-col md:block">
                                            <span className="md:hidden text-[8px] font-black text-zinc-600 uppercase tracking-widest mb-1 leading-none">Лиды (Reg)</span>
                                            <div className="flex items-center gap-2">
                                                <span className="text-base font-black text-emerald-500">{link.leads || 0}</span>
                                                <Users className="w-3 h-3 text-emerald-900 md:hidden lg:inline" />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Reward & Status */}
                                    <div className="flex items-center justify-between md:contents gap-4">
                                        <div className="md:w-24 text-center md:text-left">
                                            <span className="md:hidden text-[8px] font-black text-zinc-600 uppercase tracking-widest block mb-1">Комиссия</span>
                                            <span className="text-[11px] font-black text-zinc-300 bg-zinc-800 px-2.5 py-1.5 rounded-xl border border-zinc-700/50">
                                                {link.reward_percent}%
                                            </span>
                                        </div>

                                        <div className="md:w-40 flex justify-center">
                                            <span className={`inline-flex items-center px-4 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest border ${
                                                link.status === 'active' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
                                                link.status === 'pending' ? 'bg-amber-500/10 text-amber-400 border-amber-500/20 animate-pulse' :
                                                'bg-rose-500/10 text-rose-400 border-rose-500/20'
                                            }`}>
                                                {link.status === 'active' ? 'Активна' : link.status === 'pending' ? 'Модерация' : 'Отклонена'}
                                            </span>
                                        </div>

                                        {/* Actions */}
                                        <div className="md:w-24 flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                                            <button 
                                                onClick={() => handleCopy(link.code, link.target_type, link.target_url)}
                                                className="p-3 bg-white/5 md:bg-transparent hover:bg-white/10 rounded-xl text-zinc-400 hover:text-white transition-colors"
                                                title="Копировать ссылку"
                                            >
                                                <Copy className="w-4 h-4 md:w-5 md:h-5" />
                                            </button>
                                            <button 
                                                onClick={() => { if(window.confirm('Удалить ссылку?')) deleteMutation.mutate(link.id); }}
                                                className="p-3 bg-white/5 md:bg-transparent hover:bg-rose-500/10 rounded-xl text-rose-500/40 hover:text-rose-500 transition-colors"
                                            >
                                                <Trash2 className="w-4 h-4 md:w-5 md:h-5" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                    </div>
                )}
            </div>


            {/* Detailed Modal & Edit */}
            {selectedReferral && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-md animate-in fade-in duration-300">
                    <div className="bg-zinc-950 border border-zinc-800 w-full max-w-2xl rounded-[3rem] overflow-hidden shadow-[0_0_100px_rgba(0,0,0,0.8)] flex flex-col max-h-[90vh]">
                        
                        {/* Header */}
                        <div className="p-8 pb-4 flex justify-between items-start border-b border-zinc-900">
                            <div className="flex items-center gap-4">
                                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center border-2 ${
                                    selectedReferral.target_type === 'bot' ? 'bg-blue-600/20 border-blue-600/40 text-blue-400' : 'bg-purple-600/20 border-purple-600/40 text-purple-400'
                                }`}>
                                    {selectedReferral.target_type === 'bot' ? <Send className="w-7 h-7" /> : <LinkIcon className="w-7 h-7" />}
                                </div>
                                <div>
                                    <h3 className="text-2xl font-black text-white uppercase tracking-tight leading-none mb-1">
                                        {selectedReferral.name}
                                    </h3>
                                    <div className="flex items-center gap-2 opacity-50">
                                        <code className="text-[10px] font-mono font-bold text-zinc-400 uppercase tracking-widest bg-zinc-900 border border-zinc-800 px-2 py-0.5 rounded">
                                            CODE: {selectedReferral.code}
                                        </code>
                                        <span className="text-[10px] font-black text-zinc-600 uppercase">/ {selectedReferral.owner_name}</span>
                                    </div>
                                </div>
                            </div>
                            <button onClick={() => { setSelectedReferral(null); setIsEditMode(false); }} className="p-3 bg-zinc-900/50 hover:bg-zinc-800 rounded-2xl text-zinc-500 hover:text-white transition-all">
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="p-8 space-y-8 overflow-y-auto custom-scrollbar">
                            
                            {/* Stats Bar */}
                            <div className="grid grid-cols-3 gap-4">
                                <div className="bg-zinc-900/50 border border-zinc-800/50 p-5 rounded-[2rem] text-center group hover:bg-zinc-900 transition-colors">
                                    <p className="text-[10px] font-black text-zinc-500 uppercase tracking-widest mb-1 group-hover:text-zinc-400">Переходы</p>
                                    <p className="text-3xl font-black text-white tracking-tighter">{selectedReferral.clicks}</p>
                                </div>
                                <div className="bg-emerald-500/5 border border-emerald-500/10 p-5 rounded-[2rem] text-center group hover:bg-emerald-500/10 transition-colors">
                                    <p className="text-[10px] font-black text-emerald-900 uppercase tracking-widest mb-1 group-hover:text-emerald-700">Лиды (Reg)</p>
                                    <p className="text-3xl font-black text-emerald-500 tracking-tighter">{selectedReferral.leads}</p>
                                </div>
                                <div className="bg-zinc-900/50 border border-zinc-800/50 p-5 rounded-[2rem] text-center">
                                    <p className="text-[10px] font-black text-zinc-500 uppercase tracking-widest mb-1">Конверсия</p>
                                    <p className="text-3xl font-black text-white tracking-tighter">
                                        {selectedReferral.clicks > 0 ? ((selectedReferral.leads / selectedReferral.clicks) * 100).toFixed(1) : 0}%
                                    </p>
                                </div>
                            </div>

                            {/* Configuration / Edit */}
                            <div className="bg-zinc-900/30 border border-zinc-800/50 rounded-[2.5rem] p-6 space-y-6">
                                <div className="flex items-center justify-between">
                                    <h4 className="text-xs font-black text-zinc-400 uppercase tracking-[0.2em] flex items-center gap-2">
                                        <Clock className="w-3.5 h-3.5" /> Конфигурация ссылки
                                    </h4>
                                    {!isEditMode ? (
                                        <button 
                                            onClick={() => {
                                                setIsEditMode(true);
                                                setEditData({...selectedReferral});
                                            }}
                                            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-2 shadow-lg shadow-emerald-600/20"
                                        >
                                            <Edit3 className="w-3.5 h-3.5" /> Редактировать
                                        </button>
                                    ) : (
                                        <div className="flex gap-2">
                                            <button onClick={() => setIsEditMode(false)} className="px-4 py-2 text-zinc-500 hover:text-white transition-colors text-[10px] font-black uppercase">Отмена</button>
                                            <button 
                                                onClick={() => updateLinkMutation.mutate(editData)}
                                                disabled={updateLinkMutation.isPending}
                                                className="px-4 py-2 bg-white text-zinc-950 hover:bg-zinc-200 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-2"
                                            >
                                                <Save className="w-3.5 h-3.5" /> Сохранить
                                            </button>
                                        </div>
                                    )}
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-bold text-zinc-600 uppercase ml-2 tracking-widest">Название</label>
                                        <input 
                                            disabled={!isEditMode}
                                            value={isEditMode ? editData.name : selectedReferral.name}
                                            onChange={e => setEditData({...editData, name: e.target.value})}
                                            className="w-full bg-zinc-950 border border-zinc-800 rounded-2xl px-4 py-3 text-sm font-bold text-white outline-none focus:border-emerald-500/50 transition-all disabled:opacity-50"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-bold text-zinc-600 uppercase ml-2 tracking-widest">Вознаграждение (%)</label>
                                        <div className="relative">
                                            <input 
                                                type="number"
                                                disabled={!isEditMode}
                                                value={isEditMode ? editData.reward_percent : selectedReferral.reward_percent}
                                                onChange={e => setEditData({...editData, reward_percent: e.target.value})}
                                                className="w-full bg-zinc-950 border border-zinc-800 rounded-2xl px-4 py-3 text-sm font-black text-emerald-500 outline-none focus:border-emerald-500/50 transition-all disabled:opacity-50 font-mono"
                                            />
                                            <Percent className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-700" />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-bold text-zinc-600 uppercase ml-2 tracking-widest">Целевой ресурс</label>
                                        <input 
                                            disabled={!isEditMode}
                                            value={isEditMode ? editData.target_url : selectedReferral.target_url}
                                            onChange={e => setEditData({...editData, target_url: e.target.value})}
                                            className="w-full bg-zinc-950 border border-zinc-800 rounded-2xl px-4 py-3 text-xs font-mono text-zinc-400 outline-none focus:border-emerald-500/50 transition-all disabled:opacity-50"
                                            placeholder="@bot_name или site.com"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-bold text-zinc-600 uppercase ml-2 tracking-widest">Cookie (дней)</label>
                                        <input 
                                            type="number"
                                            disabled={!isEditMode}
                                            value={isEditMode ? editData.cookie_days : selectedReferral.cookie_days}
                                            onChange={e => setEditData({...editData, cookie_days: e.target.value})}
                                            className="w-full bg-zinc-950 border border-zinc-800 rounded-2xl px-4 py-3 text-sm font-bold text-white outline-none focus:border-emerald-500/50 transition-all disabled:opacity-50 font-mono"
                                        />
                                    </div>
                                </div>

                                {isAdmin && isEditMode && (
                                    <div className="pt-4 border-t border-zinc-800/50 grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-bold text-zinc-600 uppercase ml-2 tracking-widest">Владелец (Сотрудник)</label>
                                            <select 
                                                value={editData.owner_id || ""}
                                                onChange={e => setEditData({...editData, owner_id: e.target.value, owner_lead_id: ""})}
                                                className="w-full bg-zinc-950 border border-zinc-800 rounded-2xl px-4 py-3 text-xs font-bold text-white outline-none focus:border-emerald-500/50"
                                            >
                                                <option value="">Без владельца (Система)</option>
                                                {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                                            </select>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-bold text-zinc-600 uppercase ml-2 tracking-widest">ИЛИ Владелец (Лид)</label>
                                            <select 
                                                value={editData.owner_lead_id || ""}
                                                onChange={e => setEditData({...editData, owner_lead_id: e.target.value, owner_id: ""})}
                                                className="w-full bg-zinc-950 border border-zinc-800 rounded-2xl px-4 py-3 text-xs font-bold text-white outline-none focus:border-emerald-500/50"
                                            >
                                                <option value="">Выбрать лида...</option>
                                                {leads.map(l => <option key={l.id} value={l.id}>{l.first_name} {l.last_name} (@{l.username})</option>)}
                                            </select>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="flex gap-4">
                                <button 
                                    onClick={() => handleCopy(selectedReferral.code, selectedReferral.target_type, selectedReferral.target_url)}
                                    className="flex-1 py-4 bg-zinc-800 hover:bg-zinc-700 text-white rounded-2xl text-xs font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 active:scale-95 border border-zinc-700 shadow-xl"
                                >
                                    <Copy className="w-5 h-5" /> Копировать ссылку
                                </button>
                                {isAdmin && selectedReferral.status === 'pending' && (
                                    <button 
                                        onClick={() => updateStatusMutation.mutate({ id: selectedReferral.id, status: 'active' })}
                                        className="flex-1 py-4 bg-emerald-600 hover:bg-emerald-500 text-white rounded-2xl text-xs font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 active:scale-95 shadow-xl shadow-emerald-600/20"
                                    >
                                        <CheckCircle2 className="w-5 h-5" /> Одобрить заявку
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Create Modal */}
            {isCreateModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                    <form onSubmit={handleCreate} className="bg-zinc-950 border border-zinc-800 rounded-3xl p-6 w-full max-w-md shadow-2xl">
                        <h2 className="text-xl font-black text-white uppercase tracking-wider mb-6">Создать ссылку</h2>
                        
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-zinc-400 uppercase tracking-wider ml-1">Название кампании</label>
                                <input required value={newLink.name} onChange={e => setNewLink({...newLink, name: e.target.value})} className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white outline-none focus:border-blue-500 transition-all font-medium text-sm" placeholder="Напр: Инста Reels, Таргет..." />
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-bold text-zinc-400 uppercase tracking-wider ml-1">Код ссылки (необязательно)</label>
                                <input value={newLink.code} onChange={e => setNewLink({...newLink, code: e.target.value})} className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white outline-none focus:border-emerald-500 transition-all font-mono text-sm" placeholder="Напр: insta, reels, promo" />
                                <p className="text-[10px] text-zinc-500 px-1 italic">Если оставить пустым, будет сгенерирован автоматически.</p>
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-bold text-zinc-400 uppercase tracking-wider ml-1">Тип целевой страницы</label>
                                <select value={newLink.target_type} onChange={e => setNewLink({...newLink, target_type: e.target.value})} className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white outline-none focus:border-blue-500 transition-all font-medium text-sm">
                                    <option value="bot">Telegram Бот</option>
                                    <option value="landing">Лендинг (Сайт)</option>
                                </select>
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-bold text-zinc-400 uppercase tracking-wider ml-1">URL (Бот: @botname, Сайт: domain.com)</label>
                                <input value={newLink.target_url} onChange={e => setNewLink({...newLink, target_url: e.target.value})} className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white outline-none flex-1 font-mono text-xs focus:border-blue-500 transition-all" placeholder={newLink.target_type === 'bot' ? '@million_connections_bot' : 'millionconnections.club'} />
                            </div>

                            {isAdmin && (
                                <div className="space-y-4">
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-zinc-400 uppercase tracking-wider ml-1">Владелец (Сотрудник)</label>
                                        <select 
                                            value={newLink.owner_id} 
                                            onChange={e => setNewLink({...newLink, owner_id: e.target.value, owner_lead_id: ''})} 
                                            className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white outline-none focus:border-blue-500 transition-all font-medium text-sm"
                                        >
                                            <option value="">Выберите сотрудника...</option>
                                            <option value={currentUser.id}>Я сам ({currentUser.name})</option>
                                            {users.filter(u => u.id !== currentUser.id).map(user => (
                                                <option key={user.id} value={user.id}>{user.name} ({user.role})</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-zinc-400 uppercase tracking-wider ml-1">ИЛИ Владелец (Лид/Бот)</label>
                                        <select 
                                            value={newLink.owner_lead_id} 
                                            onChange={e => setNewLink({...newLink, owner_lead_id: e.target.value, owner_id: ''})} 
                                            className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white outline-none focus:border-emerald-500 transition-all font-medium text-sm"
                                        >
                                            <option value="">Выберите лида из базы...</option>
                                            {leads.map(lead => (
                                                <option key={lead.id} value={lead.id}>{lead.first_name} {lead.last_name} (@{lead.username})</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-zinc-400 uppercase tracking-wider ml-1 flex gap-1 items-center"><Percent className="w-3 h-3"/> Вознаграждение</label>
                                    <input type="number" step="0.1" value={newLink.reward_percent} onChange={e => setNewLink({...newLink, reward_percent: e.target.value})} className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white font-mono text-sm outline-none focus:border-blue-500" />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-zinc-400 uppercase tracking-wider ml-1 flex gap-1 items-center"><Clock className="w-3 h-3"/> Куки (дней)</label>
                                    <input type="number" value={newLink.cookie_days} onChange={e => setNewLink({...newLink, cookie_days: e.target.value})} className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white font-mono text-sm outline-none focus:border-blue-500" />
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 mt-8">
                            <button type="button" onClick={() => setIsCreateModalOpen(false)} className="px-5 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider text-zinc-400 hover:text-white hover:bg-zinc-800 transition-all">Отмена</button>
                            <button type="submit" className="px-5 py-2.5 bg-white text-black hover:bg-zinc-200 rounded-xl font-bold text-xs uppercase tracking-wider shadow-lg flex items-center gap-2 transition-all active:scale-95"><Link2 className="w-4 h-4"/> Создать</button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

export default Referrals;
